/*
 * Copyright (C) 2020 Airfordable, Inc - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 */

import { styled } from '@material-ui/styles';
import { graphql } from 'gatsby';
import React, { useState } from 'react';

import {
  ColLogo,
  DesktopColMenu,
  DesktopNavigation,
  DesktopNavigationItem,
} from './NavigationDesktop';
import {
  MobileColMenu,
  MobileContainerMenu,
  NavigationButton,
} from './NavigationMobile';
import {
  Actionable,
  ActionableProps,
  Button,
  Container,
  IconLogo,
  Typography,
} from 'components/UI';
import { Breakpoints } from 'utils/enums';
import { useBodyScrollLock } from 'utils/useBodyScrollLock';

const HeaderSection = styled('header')({
  position: 'relative',
  top: '0',
  width: '100%',
  zIndex: 99,
});

export interface HeaderMenuProps {
  link: string;
  text: string;
}

export type HeaderMode = 'dark' | 'light';

export interface HeaderProps {
  linkInstance: React.ElementType<ActionableProps>;
  menu?: HeaderMenuProps[];
  menuLabeled?: HeaderMenuProps;
  mode?: HeaderMode;
}

const BREAKPOINT = Breakpoints.Md;

const Header: React.FC<HeaderProps> = ({
  menu,
  menuLabeled,
  linkInstance,
  mode = 'light',
}) => {
  const [menuOpened, setMenuOpened] = useState(false);
  const handleMenu = () => setMenuOpened((prevState) => !prevState);

  useBodyScrollLock(menuOpened);

  return (
    <HeaderSection>
      <Container>
        <ColLogo
          container
          xs={12}
          md={3}
          alignItems="center"
          justifyContent="space-between"
        >
          <Actionable component={linkInstance} to="/">
            <IconLogo mode={mode} />
          </Actionable>

          <NavigationButton
            aria-haspopup
            onClick={handleMenu}
            active={menuOpened}
            {...(menuOpened && { 'aria-expanded': true })}
          />
        </ColLogo>

        {/* Desktop version */}
        <DesktopColMenu container xs={9} justifyContent="flex-end">
          <DesktopNavigation>
            {menu?.map(({ link, text }, index) => (
              <DesktopNavigationItem
                // @ts-ignore Due to missing types in Material UI for newer versions of typescript.
                component="div"
                variant="body2"
                key={`${link}-${text}-${index}`}
                mode={mode}
              >
                <Actionable key={text} to={link} component={linkInstance}>
                  {text}
                </Actionable>
              </DesktopNavigationItem>
            ))}
          </DesktopNavigation>

          {menuLabeled && (
            <Button
              mode={mode}
              variant="labeled"
              to={menuLabeled.link}
              component={linkInstance}
            >
              {menuLabeled.text}
            </Button>
          )}
        </DesktopColMenu>

        {/* Mobile version */}
        <MobileColMenu active={menuOpened}>
          <MobileContainerMenu>
            {menuLabeled && (
              <Typography component="p" variant="h4">
                <Actionable to={menuLabeled.link} component={linkInstance}>
                  {menuLabeled.text}
                </Actionable>
              </Typography>
            )}
            {menu?.map(({ link, text }, index) => (
              <Typography
                variant="h4"
                component="p"
                key={`${link}-${text}-${index}`}
              >
                <Actionable key={text} to={link} component={linkInstance}>
                  {text}
                </Actionable>
              </Typography>
            ))}
          </MobileContainerMenu>
        </MobileColMenu>
      </Container>
    </HeaderSection>
  );
};

const query = graphql`
  fragment Header on DefaultYamlNavigation {
    header {
      menu {
        link
        text
      }
      menuLabeled {
        link
        text
      }
    }
  }
`;

export { BREAKPOINT, Header, query };
